// Content data for Dr Joseph Cheriyan lab website

export const site = {
  name: 'Cheriyan Group',
  short: 'Cambridge · Vascular Function & Clinical Trials',
  tagline: 'CAMBRIDGE UNIVERSITY HOSPITALS · UNIVERSITY OF CAMBRIDGE',
  university: 'University of Cambridge',
};

export const partnerLogos = [
  {
    name: 'Cambridge Clinical Trials Unit',
    src: '/logos/cctu.svg',
    href: 'https://cctu.org.uk/',
    height: 56,
  },
  {
    name: 'University of Cambridge',
    src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/2ym80rrn_CleanShot%202026-07-20%20at%2023.11.52%402x.png',
    href: 'https://www.cam.ac.uk/',
    height: 56,
  },
  {
    name: 'Cambridge University Hospitals NHS Foundation Trust',
    src: 'https://media.cuh.nhs.uk/images/CUH_logo_Digital_LEFT_RGB3709.width-200.png',
    href: 'https://www.cuh.nhs.uk/',
    height: 56,
  },
  {
    name: 'University of East Anglia',
    src: '/logos/uea.png',
    href: 'https://www.uea.ac.uk/study/subjects/medicine',
    height: 56,
  },
  {
    name: 'Cambridge Medical Seminars',
    src: 'https://cambsems.org/assets/images/cambridgemedicalseminars2.jpg',
    href: 'https://cambsems.org/',
    height: 56,
  },
];

// Real official photo (CUH staff directory)
export const CHERIYAN_PHOTO = '/team/joseph-cheriyan.jpg';
export const CHERIYAN_PHOTO_ALT = 'https://www.cardiovascular.cam.ac.uk/sites/default/files/styles/inline/public/images/profile/cheriyan.jpg?itok=NvvKfVle';

export const nav = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  {
    label: 'Research',
    to: '/research',
    children: [
      { label: 'Research themes', to: '/research' },
      { label: 'AI & digital biomarkers', to: '/ignite' },
      { label: 'Trials portfolio', to: '/trials' },
      { label: 'SHE-HEALS · women\u2019s CV health', to: '/she-heals' },
    ],
  },
  {
    label: 'Capability & Infrastructure',
    to: '/echo',
    children: [
      { label: 'Research Echo Service', to: '/echo' },
      { label: 'Research Procedures', to: '/research-procedures' },
      { label: 'Cardiovascular Trials Office', to: '/trials-office' },
    ],
  },
  { label: 'Team', to: '/team' },
  { label: 'News', to: '/news' },
  { label: 'Publications', to: '/publications' },
  { label: 'Contact', to: '/contact' },
];

export const hero = {
  eyebrow: 'CAMBRIDGE BIOMEDICAL CAMPUS',
  titleStart: 'Bridging Bench to Bedside in',
  titleAccent: 'Cardiovascular',
  titleEnd: '& Experimental Medicine',
  description:
    "Led by Dr Joseph Cheriyan at Cambridge University Hospitals and the University of Cambridge, the Cheriyan Group designs and delivers first-in-human and early-phase clinical trials and experimental-medicine studies, investigating novel and repurposed therapeutics across cardiovascular, respiratory and immunometabolic disease. Its AI and digital-innovation work runs under IGNITE — Innovative Group for Novel Interventions, Trials & Experimental Medicine — the group's Cambridge programme for intelligent, technology-enabled trials and biomarkers.",
  image: '/videos/echo_home.mp4',
  stats: [
    { value: '2006', suffix: '–', label: 'Consultant at CUH' },
    { value: 'MHRA', suffix: '', label: 'MHRA-accredited Principal Investigator for first-in-human trials' },
    { value: '20', suffix: '+', label: 'Phase 1/2 studies led' },
    { value: '19', suffix: '', label: 'Group & Trials Office members' },
  ],
};

export const affiliations = [
  'Cambridge University Hospitals NHS Foundation Trust',
  'University of Cambridge, Department of Medicine',
  'Cambridge Clinical Trials Unit (CCTU)',
  'NIHR Cambridge Biomedical Research Centre',
  "Vascular Research Clinics, Addenbrooke's Hospital",
  'BHF Cambridge Centre of Research Excellence',
  'Norwich Medical School, University of East Anglia',
];

export const about = {
  eyebrow: 'PRINCIPAL INVESTIGATOR',
  name: 'Dr Joseph Cheriyan',
  credentials: 'MBChB, MA, FRCP, FESC, FACC',
  role: 'Consultant Clinical Pharmacologist & Physician',
  affiliation: 'Affiliated Associate Professor, University of Cambridge',
  intro:
    "Dr Cheriyan is a clinician-scientist whose research programme evaluates novel and repurposed therapeutics in humans — spanning first-in-human safety studies through to proof-of-efficacy trials — with a particular focus on cardiovascular medicine (hypertension, heart failure, acute coronary syndromes, dyslipidaemia and vascular inflammation) and respiratory medicine (chronic obstructive pulmonary disease and COVID-19). His work sits at the interface of experimental medicine, clinical pharmacology and translational cardiovascular science, and is supported by a portfolio of investigator-led trials, industry-sponsored studies and academic collaborations across the University of Cambridge and Cambridge University Hospitals. He also holds a Visiting Researcher appointment at Norwich Medical School, University of East Anglia.",
  paragraphs: [
    "Since December 2013 he has served as Director and Theme Lead of the Cardiovascular Trials Office at the Cambridge Clinical Trials Unit (CCTU), leading the University-hosted trials infrastructure that supports the group's investigator-led and academic-industry cardiovascular studies.",
    "From 2007 to 2020 Dr Cheriyan held a 13.5-year, part-time (0.5 FTE) NHS secondment to the GlaxoSmithKline Clinical Unit Cambridge, serving as an investigator on numerous first-in-human and Phase I/II industry-sponsored trials — an experience that continues to shape the design, governance and translational ambition of the group's current programme.",
    "He directs a substantial postgraduate education portfolio for junior doctors and clinician scientists, including the Cambridge Medical Seminars and the Experimental Medicine Training Initiative (EMI); since 2018 this has extended internationally through joint courses delivered in partnership with the Royal College of Physicians across South-East Asia.",
    "Clinically, Dr Cheriyan is the senior consultant in the Department of Clinical Pharmacology at Cambridge University Hospitals NHS Foundation Trust, where he contributes to the acute-medical intake at Addenbrooke's Hospital and runs hypertension and general-medicine outreach clinics at Addenbrooke's and Saffron Walden.",
  ],
  mission:
    'The group\'s purpose is to translate rigorous experimental medicine into therapies that reach the patients who need them — through international, collaborative and uncompromisingly high-quality clinical trials.',
  image: CHERIYAN_PHOTO,
  imageAlt: CHERIYAN_PHOTO_ALT,
};

export const positions = [
  { years: 'Sep 2006 – present', title: 'Consultant Physician in Clinical Pharmacology & General Internal Medicine', org: 'Cambridge University Hospitals NHS Trust' },
  { years: 'Nov 2006 – present', title: 'Founder, Cambridge Medical Seminars & RCP-Sunway-Cambridge Medical Seminars', org: '' },
  { years: 'Dec 2013 – present', title: 'Director & Theme Lead, Cardiovascular Clinical Trials Office', org: 'Cambridge Clinical Trials Unit' },
  { years: 'May 2016 – present', title: 'Lead, Experimental Medicine Training Initiative', org: 'University of Cambridge' },
  { years: 'Sep 2016 – present', title: 'Director, Research Echocardiography Service', org: 'Vascular Research Clinics, CUH' },
  { years: 'May 2019 – present', title: 'Member, Clinical Pharmacology & Therapeutics SAC', org: 'JRCPTB' },
  { years: 'May 2019 – 2026', title: 'Training Programme Director, Clinical Pharmacology & Therapeutics', org: 'NHS England (East of England)' },
  { years: 'Feb 2020 – present', title: 'Adviser, Phase 1 Trials Scientific Review Panel', org: 'Malaysian Ministry of Health' },
  { years: 'Oct 2022 – present', title: 'Affiliated Associate Professor', org: 'Department of Medicine, University of Cambridge' },
  { years: 'Apr 2024 – present', title: 'Adviser, Joint Scientific Committee for Phase 1 Clinical Trials', org: 'Hong Kong' },
  { years: 'present', title: 'Visiting Researcher', org: 'Norwich Medical School, University of East Anglia' },
];

export const pastRoles = [
  { years: 'Mar 2007 – Sep 2020', title: 'Senior Clinical Pharmacologist & MHRA-Accredited Phase 1 Physician', org: 'GSK Clinical Unit Cambridge (50% NHS secondment)' },
  { years: 'Jul 2010 – Apr 2013', title: 'Senior Internal Examiner, Wellcome Trust Translational Medicine & Therapeutics programme', org: 'University of Cambridge' },
  { years: 'Jul 2010 – Oct 2022', title: 'Associate Lecturer / Affiliated Assistant Professor', org: 'University of Cambridge' },
  { years: 'May 2012 – May 2022', title: 'Vice Chair, Cambridge Central Research Ethics Committee', org: 'Health Research Authority' },
];

export const memberships = [
  'Fellow, European Society of Cardiology (FESC)',
  'Fellow, American College of Cardiology (FACC)',
  'Fellow, Royal College of Physicians (FRCP)',
  'Member, British & Irish Hypertension Society',
];

export const researchSummary = {
  text:
    "Dr Cheriyan's group investigates vascular function and inflammation, nitric oxide bioavailability and vascular–ventricular interactions in health and disease. Combining in-vivo human physiology, molecular imaging and early-phase clinical trials — increasingly augmented by AI and digital biomarkers through IGNITE (Innovative Group for Novel Interventions, Trials & Experimental Medicine) — the group identifies novel and repurposed agents and develops new ways to detect and monitor disease across cardiovascular, respiratory and immunometabolic medicine.",
};

export const researchThemes = [
  {
    name: 'Vascular function, NO bioavailability and the microcirculation',
    description:
      "Forearm plethysmography, flow-mediated dilatation, arterial stiffness and 24-hour central ambulatory blood pressure characterise the role of vasoactive peptides and hormones in the regulation of vascular tone. Bespoke conjunctival vessel imaging provides a non-invasive readout of the microcirculation, complementing macrovascular measures. The strength of these clinical physiological studies lies in their direct relevance to human biology — anchoring novel drug development in human pathophysiology. Current work probes endothelin-A blockade combined with SGLT2 inhibition, apelin and relaxin agonism, and dual incretin agonism.",
  },
  {
    name: 'Cardiovascular imaging',
    description:
      "Molecular and functional cardiovascular imaging — FDG PET-CT, immuno-PET, cardiac MRI, hyperpolarised MRI and dynamic contrast-enhanced MRI — is applied to atherosclerosis biology, off-target pharmacology in inflammatory disease, and drug-induced changes in the vessel wall and myocardium. These techniques provide non-invasive, mechanistic biomarkers for early-phase and multi-centre trials.",
  },
  {
    name: 'Inflammation and immunometabolism',
    description:
      "A flagship programme addresses the residual inflammatory risk that persists despite optimal LDL-lowering, with low-dose interleukin-2 emerging as a first-in-class immunomodulatory strategy for acute coronary syndromes. Single-cell readouts, molecular imaging and deep immune phenotyping interrogate T-cell biology, regulatory-B-cell responses and p38 MAPK signalling. Related work examines colchicine, anti-TNF-α therapy and other cardiometabolic anti-inflammatories.",
  },
  {
    name: 'Hypertension, lipids and cardiometabolic medicine',
    description:
      "Work spans ancestry-informed selection of first-line antihypertensive drugs, novel antihypertensive mechanisms including aldosterone-synthase inhibition, PCSK9-mediated intensive LDL-lowering, and mechanisms of oncology-drug-induced hypertension. Related interests include hypertensive disorders of pregnancy, isometric exercise training in HFpEF and community cardiovascular risk reduction in under-served populations.",
  },
  {
    name: 'First-in-human and early-phase clinical trials',
    description:
      "Working across the University and NHS domains since 2006, over twenty Phase 1/2 studies have been led, spanning novel biologics, small molecules and oligonucleotides. Studies embed proof-of-mechanism and proof-of-concept pharmacodynamic markers to discharge risk, and often repurpose licensed drugs for novel indications where they are otherwise contraindicated.",
  },
  {
    name: 'Multi-centre, pandemic-era and international trials',
    description:
      "Beyond single-site experimental medicine, the group leads and contributes to large multi-centre and international trials — most notably the UK's Urgent Public Health-designated COVID-19 programmes during the pandemic, and international Phase 1 capacity-building across Southeast Asia through advisory work with the Malaysian Ministry of Health and the Hong Kong Joint Scientific Committee for Phase 1 Clinical Trials.",
  },
];

export const projectActivity = [
  {
    name: 'IVORY (low-dose IL-2 in acute coronary syndrome)',
    description:
      "Randomised Phase 2 trial of low-dose interleukin-2 in acute coronary syndrome, with follow-on mechanistic work on regulatory T-cell and B-cell biology. Chief Investigator; Medical Research Council-funded.",
  },
  {
    name: 'Zibotentan and dapagliflozin in resistant hypertension',
    description:
      "Phase 2 randomised study of endothelin-A blockade combined with SGLT2 inhibition in resistant hypertension. Principal Investigator; academic–industry collaboration.",
  },
  {
    name: 'LAUNCH-HTN and open-label extension (lorundrostat)',
    description:
      "Phase 3 evaluation of aldosterone-synthase inhibition for uncontrolled and treatment-resistant hypertension. Principal Investigator; industry-sponsored.",
  },
  {
    name: 'AIM-HY INFORM',
    description:
      "Ancestry-informed first-line antihypertensive selection; findings have contributed to UK hypertension guidance. Cambridge Principal Investigator; Medical Research Council-funded.",
  },
  {
    name: 'INTENSITY',
    description:
      "Mechanistic randomised study of intensive LDL-cholesterol lowering with a PCSK9 monoclonal antibody. Chief Investigator.",
  },
  {
    name: 'DEFINE',
    description:
      "Pilot haemodynamic study of apelin and relaxin agonism in healthy volunteers.",
  },
  {
    name: 'Cambridge Research Echocardiography Service',
    description:
      "EACVI-accredited high-end research echocardiography laboratory supporting more than one hundred oncology and cardiology trials. Director since 2016.",
  },
  {
    name: 'Cardiac amyloidosis programme',
    description:
      "Pharmacodynamic evaluation and immuno-PET substudy of anti-SAP antibodies in cardiac amyloidosis.",
  },
  {
    name: 'SHE-HEALS',
    description:
      "Community cardiovascular risk reduction study in under-served populations. Phase 4, in progress.",
  },
  {
    name: 'TACTIC-E, TACTIC-R and PROLIFIC',
    description:
      "Urgent Public Health-designated COVID-19 trial programme delivered during the pandemic; TACTIC-E led as international Chief Investigator.",
  },
  {
    name: 'ERICA',
    description:
      "COPD longitudinal cohort study of inflammation and cardiovascular risk. Sub-investigator.",
  },
  {
    name: 'International Phase 1 advisory work',
    description:
      "Adviser to the Malaysian Ministry of Health Phase 1 review panel (since 2020) and the Hong Kong Joint Scientific Committee for Phase 1 Clinical Trials (since 2024).",
  },
];

export const cto = {
  eyebrow: 'SUPPORTING THE CLINICAL TRIAL ENVIRONMENT',
  title: 'Cardiovascular Trials Office, Cambridge CTU',
  body: 'The Cardiovascular Trials Office is one of the disease-area themes of the Cambridge Clinical Trials Unit (CCTU) — a UKCRC-registered clinical trials unit supported by the National Institute for Health and Care Research (NIHR). Directed by Dr Joseph Cheriyan since 2013, the office leads the design, implementation and delivery of clinical trials on campus, including experimental studies in the cardio-respiratory arena aligned to the NIHR Cambridge Biomedical Research Centre theme.',
  points: [
    'UKCRC-registered CTU infrastructure (Cambridge CTU) with full NIHR quality-management alignment',
    'End-to-end trial design, sponsorship, statistical planning and delivery',
    'Academic-sponsored and academic–industry collaborative trials — Phase 1 to Phase 4',
    'Cardio-respiratory experimental medicine aligned to the NIHR Cambridge BRC theme',
  ],
};

export const echoService = {
  eyebrow: 'RESEARCH ECHO SERVICE',
  title: 'Research Echo Service, Cambridge',
  tagline: 'Advancing Cardiovascular Research Through High-Quality Diagnostic Services',
  intro:
    'The Research Echo Service, Cambridge is a specialist cardiovascular diagnostics and clinical-research core-lab-style facility dedicated to supporting academic, NHS and commercial clinical trials. Established in 2016 and EACVI-accredited since 2019, our mission is to provide high-quality, protocol-driven cardiovascular investigations that enable researchers and sponsors to deliver studies efficiently while maintaining the highest standards of patient care and data quality.',
  body: [
    'Led by experienced and EACVI-accredited cardiac physiologists, the Research Echo Service combines clinical expertise with research excellence to support studies ranging from early-phase drug development to multicentre outcome trials.',
    'The service provides a comprehensive range of non-invasive cardiovascular investigations, performed in accordance with study protocols, Good Clinical Practice (GCP), and sponsor or core-laboratory requirements.',
  ],
  servicesHeading: 'Our Services',
  servicesIntro:
    'The Research Echo Service provides a comprehensive cardiovascular diagnostic offering, including:',
  services: [
    'Transthoracic echocardiography (2D and 3D)',
    'Cardiac safety echocardiography',
    'Serial protocol echocardiography',
    '12-lead electrocardiography (ECG)',
    'Beat-to-beat haemodynamic assessment',
    'Vascular ultrasound assessment (FMD)',
    'DICOM image acquisition, storage and transfer to sponsor core laboratories',
    'Cardiovascular safety assessments for Serious Adverse Events (SAEs)',
  ],
  servicesFooter:
    'Our flexible service model allows investigations to be performed within research facilities, private hospitals, NHS sites or dedicated research units, helping minimise delays associated with external referrals and improving protocol compliance.',
  conditionsHeading: 'Conditions and Therapeutic Areas',
  conditionsIntro:
    'We support research across a broad range of cardiovascular and metabolic diseases, including:',
  conditions: [
    'Heart failure',
    'Cardiomyopathy',
    'Coronary artery disease',
    'Atrial fibrillation and cardiac arrhythmias',
    'Hypertension',
    'Pulmonary hypertension',
    'Structural heart disease',
    'Valvular heart disease',
    'Cardio-oncology',
    'Diabetes and cardiometabolic disease',
  ],
  researchSupportHeading: 'Research Support',
  researchSupportIntro:
    'The clinic has extensive experience supporting commercial and investigator-led clinical trials. We work closely with pharmaceutical companies, contract research organisations (CROs), medical device manufacturers and academic institutions to deliver high-quality cardiovascular endpoint assessments.',
  researchSupportSubheading: 'Our research support includes:',
  researchSupport: [
    'Protocol feasibility and cardiovascular endpoint planning',
    'Study-specific imaging protocols',
    'Site initiation and investigator training',
    'Standardised image acquisition',
    'Quality assurance and image review',
    'DICOM transfer to sponsor or independent core laboratories',
    'Rapid reporting and turnaround times',
    'Cardiovascular safety monitoring throughout the study',
  ],
  qaHeading: 'Quality Assurance & EACVI Accreditation',
  qaBody: [
    'The laboratory is accredited by the European Association of Cardiovascular Imaging (EACVI) of the European Society of Cardiology — the international benchmark for echocardiography quality. EACVI Laboratory Accreditation certifies that a service meets rigorous standards for image acquisition, reporting, staff qualifications, equipment maintenance and continuous quality improvement.',
    'Accreditation is granted only after independent peer review of representative studies, standard operating procedures and departmental audit outputs. Cambridge is one of a small number of UK laboratories to hold current EACVI accreditation, providing sponsors, investigators and core laboratories with assurance that trial-grade cardiovascular data are being generated to a consistent, internationally recognised standard.',
  ],
  qaPoints: [
    'Studies performed by BSE- or EACVI-accredited cardiac physiologists',
    'Written standard operating procedures for every acquisition and reporting workflow',
    'Regular internal audit and inter-reader concordance review',
    'Blinded core-lab-style offline analysis where required',
    'Full DICOM traceability and audit trail from acquisition to sponsor transfer',
    'Ongoing continuing professional development for all staff',
  ],
  eacviLogo: '/logos/eacvi.png',
  accreditationHref:
    'https://www.escardio.org/about-us/who-we-are/supporting-excellence/accreditation/eacvi-laboratory-accreditation-in-echocardiography/eacvi-echo-accredited-laboratories/',
  team: [
    { name: 'Dr Joseph Cheriyan', role: 'Director' },
    { name: 'Navazh Jalaludeen', role: 'Consultant Cardiac Physiologist (Echocardiography) · Service Lead' },
  ],
  images: [
    { src: '', alt: '3D echocardiography of the aortic valve', caption: '3D echocardiography — aortic valve' },
    { src: '', alt: '2D echocardiography with colour Doppler', caption: '2D imaging with colour Doppler' },
  ],
  videos: [
    { src: '/videos/echo_clip2_cropped.mp4', caption: 'Research echocardiography — clip 1' },
    { src: '/videos/echo_clip3_cropped.mp4', caption: 'Research echocardiography — clip 2' },
    { src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/ibdhbczb_WhatsApp%20Video%202026-07-21%20at%202.04.29%20PM%20%283%29.mp4', caption: 'Research echocardiography — clip 3' },
    { src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/n8yajyu1_WhatsApp%20Video%202026-07-21%20at%202.04.29%20PM%20%284%29.mp4', caption: 'Research echocardiography — clip 4' },
  ],
};

export const teaching = {
  eyebrow: 'TEACHING & TRAINING',
  title: 'Educating the Next Generation of Clinician Scientists',
  body: 'Dr Cheriyan leads an active programme of postgraduate education for junior doctors and clinician scientists, from bedside teaching in Cambridge to international courses across Southeast Asia. Since 2018 the programme has expanded internationally through joint courses with the Royal College of Physicians.',
  programmes: [
    {
      name: 'Cambridge Medical Seminars',
      href: 'https://cambsems.org/',
      role: 'Founder · 2006–present',
      description: 'A long-running clinical seminar series for medical trainees and consultants covering acute and general medicine, hypertension and cardiovascular risk.',
    },
    {
      name: 'Experimental Medicine Training Initiative (EMI)',
      href: 'https://otr.medschl.cam.ac.uk/project-management/experimental-medicine-initiative-emi',
      role: 'Lead · 2016–present',
      description: "Cambridge's doctoral-level training scheme for clinical and non-clinical students in experimental medicine, translational science and early-phase trials.",
    },
    {
      name: 'RCP-Sunway-Cambridge Medical Seminars',
      href: 'https://cambsems.org/sunway-rcp-cambridge-medical-seminars-past-seminars-and-taster-webinars',
      role: 'Co-founder · 2018–present',
      description: 'International medical education programme delivered jointly with the Royal College of Physicians and Sunway Medical Centre in Southeast Asia.',
    },
    {
      name: 'Clinical Pharmacology & Therapeutics — East of England',
      href: 'https://www.jrcptb.org.uk/specialties/clinical-pharmacology-and-therapeutics',
      role: 'Training Programme Director · 2019–2026',
      description: 'Directed the specialty training pathway for CP&T across the East of England for NHS England (2019–2026) and continues to sit on the JRCPTB Specialist Advisory Committee.',
    },
  ],
};

export const featuredTrial = {
  eyebrow: 'FLAGSHIP PROGRAMME',
  title: 'The IVORY Programme',
  body: 'IVORY (Interleukin-2 for the reduction of Vascular inflammation in acute cORonary sYndromes) and its predecessor LILACS delivered the first randomised evidence that low-dose IL-2 safely expands regulatory T cells and reshapes the immune response following acute coronary syndrome — published in Nature Medicine (2026) and NEJM Evidence (2022).',
  body2:
    'The programme knits together mechanistic experimental medicine, deep immune phenotyping and multi-centre clinical delivery — a blueprint for how UK academic cardiology can move from concept to landmark trial.',
  stats: [
    { value: '£1.86M', label: 'MRC DPFS award' },
    { value: 'Nat Med', label: '2026 Phase 2 readout' },
    { value: 'Multi', label: 'centre UK trial' },
    { value: 'MRC', label: 'Nationally funded' },
  ],
  image: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/0nz2dmwl_Screenshot%202026-07-21%20at%2017.53.12.png',
  teamPhoto: '/team/ivory-team.jpg',
  teamPhotoCaption: "The IVORY trial team at the Addenbrooke's clinical research campus, Cambridge.",
  video: {
    provider: 'youtube',
    id: 'fWd5BNFziko',
    url: 'https://www.youtube.com/watch?v=fWd5BNFziko',
    title: 'BBC coverage of the IVORY trial',
    caption: 'BBC News reports on the Cambridge-led IVORY trial \u2014 low-dose IL-2 to dampen inflammation after a heart attack.',
  },
};

export const trials = {
  academic: [
    { name: 'STEREO (AI voice/sound analysis for heart failure)', nct: 'NCT06555757', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2024–', phase: 'Prospective observational' },
    { name: 'IVORY-FINALE (low-dose IL-2 · vascular inflammation in ACS · outcomes)', nct: 'NCT06427694', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2024–', phase: 'Phase 3 outcomes' },
    { name: 'DECODE (semaglutide & tirzepatide haemodynamics)', nct: 'NCT07483801', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2026–', phase: 'Phase 4 pilot' },
    { name: 'ELIMINATE (influenza vaccination after MI · cardiac inflammation)', nct: 'NCT06336317', funder: 'Region Örebro County · multi-centre', amount: '—', role: 'Principal Investigator (Cambridge)', years: '2024–', phase: 'Phase 4 RCT' },
    { name: 'SHE-HEALS', nct: '—', funder: 'GCRFF', amount: '£7,400,000', role: 'Grant Co-applicant and Trial CI', years: '2026–', phase: 'Multi-centre observational + detection trial' },
    { name: 'TRANSPARENT (zibotentan + dapagliflozin in resistant hypertension)', nct: 'ISRCTN10215858', funder: 'AstraZeneca', amount: '£122,867', role: 'Grant applicant & PI', years: '2025–', phase: 'Phase 2 pilot RCT' },
    { name: 'TACTIC-E (COVID-19)', nct: 'NCT04393246', funder: 'AstraZeneca / Evelo / Union', amount: '£6,494,653', role: 'International Chief Investigator', years: '2020–22', phase: 'Multi-centre international' },
    { name: 'TACTIC-R (COVID-19)', nct: 'NCT04390464', funder: 'LifeArc / Eli Lilly / Alexion', amount: '£1,300,000', role: 'TMG & co-applicant', years: '2020–22', phase: 'Multi-centre national' },
    { name: 'PROLIFIC (COVID-19)', nct: 'NCT04352933', funder: 'Evolution Education Trust', amount: '£300,000', role: 'Chief Investigator', years: '2020', phase: 'Chemoprophylaxis' },
    { name: 'IVORY (low-dose IL-2 in ACS)', nct: 'NCT04241601', funder: 'MRC DPFS', amount: '£1,860,000', role: 'Chief Investigator', years: '2020–24', phase: 'Phase 2 randomised trial' },
    { name: 'LILACS (low-dose IL-2 in ischaemic heart disease)', nct: 'NCT03113773', funder: 'MRC DPFS', amount: '£520,000', role: 'Chief Investigator', years: '2017–19', phase: 'Phase 1b / mechanistic' },
    { name: 'AIMHY-INFORM', nct: 'NCT02847338', funder: 'Medical Research Council', amount: '£3,504,917', role: 'PI (Cambridge)', years: '2016–24', phase: 'Hypertension by ancestry' },
    { name: 'INTENSITY-HIGH', nct: 'NCT03355027', funder: 'JP Moulton Charitable Foundation', amount: '£985,370', role: 'Grant applicant & CI', years: '2017–22', phase: 'PCSK9 mechanistic RCT' },
    { name: 'INTENSITY-LOW', nct: 'NCT03273972', funder: 'JP Moulton Charitable Foundation', amount: '£985,370', role: 'Grant applicant & CI', years: '2016–20', phase: 'PCSK9 mechanistic RCT' },
    { name: 'DEFINE (Apelin / Relaxin)', nct: 'NCT03449251', funder: 'AstraZeneca', amount: '£488,718', role: 'Grant applicant', years: '2016–26', phase: 'Pilot haemodynamic' },
    { name: 'PRIME (PK/PD in cardiovascular disease)', nct: 'NCT03098680', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2017–20', phase: 'Phase 1' },
    { name: 'REVEAL-Vasc (anacetrapib on vascular function)', nct: 'NCT02931188', funder: 'CUH · University of Cambridge · MSD', amount: '—', role: 'Chief Investigator', years: '2017–18', phase: 'Mechanistic sub-study' },
    { name: 'OPERA-CKD (ESAs · foetal haemoglobin in CKD)', nct: 'NCT02987465', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2017–18', phase: 'Mechanistic' },
    { name: 'VERIFY (18F-FDG PET/CT in inflammation)', nct: 'NCT03312712', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2018–22', phase: 'Imaging validation' },
    { name: 'EVOLUTION (losmapimod in COPD)', nct: 'NCT01541852', funder: 'CUH · University of Cambridge · GSK', amount: '—', role: 'Principal Investigator', years: '2012–14', phase: 'Phase 2' },
    { name: 'APELIN-COPD (apelin agonist/antagonist haemodynamics)', nct: 'NCT02129309', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2014', phase: 'Mechanistic' },
    { name: 'TomVasc (tomato extract · vascular effects)', nct: 'NCT01100385', funder: 'CUH · University of Cambridge', amount: '—', role: 'Chief Investigator', years: '2010–12', phase: 'Mechanistic RCT' },
    { name: 'ERICA (COPD inflammation)', nct: '—', funder: 'Innovate UK', amount: '£2,865,879', role: 'Grant co-applicant', years: '2011–15', phase: 'Multi-centre observational' },
  ],
  commercial: [
    { name: 'ZENITH (zilebesiran in hypertension + CVD)', nct: 'NCT07181109', funder: 'Alnylam Pharmaceuticals', amount: '—', role: 'Principal Investigator', years: '2025–', phase: 'Phase 3 CV outcomes' },
    { name: 'AZURE OUTCOMES (AZD0780 in ASCVD)', nct: 'NCT07000357', funder: 'AstraZeneca', amount: '—', role: 'Principal Investigator', years: '2025–', phase: 'Phase 3 MACE outcomes' },
    { name: 'Lorundrostat OLE study', nct: 'NCT05968430', funder: 'Mineralys Therapeutics', amount: '£150,000', role: 'Principal Investigator', years: '2025–', phase: 'Phase 3 hypertension' },
    { name: 'Lorundrostat efficacy & safety (LAUNCH-HTN)', nct: 'NCT06153693', funder: 'Mineralys Therapeutics', amount: '£220,000', role: 'Principal Investigator', years: '2024–25', phase: 'Phase 3 uncontrolled HTN' },
    { name: 'ECG Patch Multidomain study', nct: 'NCT06243029', funder: 'AstraZeneca', amount: '£210,000', role: 'Chief Investigator', years: '2024–25', phase: 'Device / cardiology' },
    { name: 'Daprodustat vs darbepoetin alfa', nct: 'NCT03446612', funder: 'GSK', amount: '—', role: 'Chief Investigator', years: '2018–20', phase: 'Forearm blood flow' },
    { name: 'GSK2798745 dosing study', nct: 'NCT02119260', funder: 'GSK', amount: '£1,300,000', role: 'Principal Investigator', years: '2014–16', phase: 'Phase I' },
    { name: 'GW856553 FDG-PET/CT (losmapimod)', nct: 'NCT00633022', funder: 'GSK', amount: '£2,050,000', role: 'Chief Investigator', years: '2009–10', phase: 'Phase IIa' },
  ],
};

export const sheHeals = {
  eyebrow: 'FLAGSHIP GLOBAL PROGRAMME · WOMEN\u2019S CARDIOVASCULAR HEALTH',
  acronym: 'Stopping atherosclerotic Heart and vEssel disease in women',
  title: 'SHE-HEALS',
  tagline: 'A global, first-of-its-kind study of how menopause shapes heart health \u2014 and whether earlier detection and prevention can stop, or even reverse, silent atherosclerosis in women.',
  heroImage: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/g506i86j_IMG-20260729-WA0012.jpg',
  heroImageAlt: 'A ring of women\u2019s hands from many skin tones coming together to form the shape of a heart, above a pink ribbon \u2014 symbolising SHE-HEALS as an international collaboration focused on women\u2019s cardiovascular health.',
  hero: {
    body: 'SHE-HEALS is a £7.4 million, five-year international programme awarded in January 2026 through the Global Cardiovascular Research Funders Forum (GCRFF) International Research Challenge on Women\u2019s Cardiovascular Health. The network spans thirteen institutions across seven countries and is jointly led from the University of Cambridge and the University of Melbourne. Dr Joseph Cheriyan is a Grant Co-applicant and Trial Chief Investigator.',
    body2: 'The programme sets out to characterise the vascular biology of the perimenopause, define how age at menopause and hormone therapy shape cardiovascular risk, and deliver the largest trial of its kind to detect silent atherosclerosis in women in perimenopause and early after menopause \u2014 testing whether earlier, targeted prevention can halt or reverse progression before symptoms appear.',
  },
  stats: [
    { value: '£7.4M', label: 'GCRFF award (Jan 2026)' },
    { value: '5 yrs', label: 'From May 2026' },
    { value: '13', label: 'Institutions' },
    { value: '7', label: 'Countries' },
  ],
  aims: [
    {
      title: 'Characterise the perimenopausal vasculature',
      body: 'Use cutting-edge imaging, immune phenotyping and molecular biology to map the arterial changes that emerge during the menopausal transition and drive lifetime cardiovascular risk.',
    },
    {
      title: 'Define how menopause timing and HRT modify risk',
      body: 'Investigate how age at menopause \u2014 and whether women take hormone replacement therapy \u2014 shapes the trajectory of atherosclerotic disease.',
    },
    {
      title: 'Detect silent atherosclerosis earlier',
      body: 'Establish the largest clinical trial of its kind to identify hidden hardening of the arteries in perimenopausal and early post-menopausal women before symptoms appear.',
    },
    {
      title: 'Test whether early prevention halts or reverses disease',
      body: 'Evaluate whether targeting key risk factors \u2014 blood pressure, cholesterol and other modifiable pathways \u2014 can stop or reverse atherosclerotic progression, closing a widely-missed prevention window in women\u2019s cardiovascular care.',
    },
  ],
  cambridgeRole: {
    title: 'Cambridge role',
    body: 'The Cambridge site is led by BHF Professor of Cardiovascular Medicine Ziad Mallat, who directs the SHE-HEALS programme with Professor Martha Hickey in Melbourne. Dr Joseph Cheriyan is a Grant Co-applicant and Trial Chief Investigator, with the Cambridge University Hospitals Cardiovascular Trials Office providing clinical trial delivery, imaging and coordination infrastructure to the UK arm of the study.',
    href: '/trials-office',
    linkLabel: 'Cardiovascular Trials Office',
  },
  leads: [
    {
      name: 'Professor Ziad Mallat',
      role: 'Global co-lead \u00b7 University of Cambridge',
      body: 'BHF Professor of Cardiovascular Medicine at the University of Cambridge and Director of Research at Inserm, France. Elected to the Academy of Medical Sciences for sustained, internationally recognised contributions to cardiovascular immunology and atherosclerosis.',
      href: 'https://www.hlri.cam.ac.uk/staff/professor-ziad-mallat',
    },
    {
      name: 'Professor Martha Hickey',
      role: 'Global co-lead \u00b7 University of Melbourne',
      body: 'Gynaecologist and researcher who leads Australia\u2019s largest menopause service. Clinical expert for the 2024 UK NICE guidelines on menopause and senior editor for the Cochrane Collaboration, with more than 300 peer-reviewed papers.',
      href: 'https://findanexpert.unimelb.edu.au/profile/340903-martha-hickey',
    },
    {
      name: 'Dr Joseph Cheriyan',
      role: 'Grant Co-applicant \u00b7 Trial Chief Investigator',
      body: 'Consultant Physician & Clinical Pharmacologist at Cambridge University Hospitals and the University of Cambridge, and Director of the Cardiovascular Trials Office. Leads the UK trial arm of SHE-HEALS.',
      href: '/about',
    },
  ],
  funders: {
    title: 'Funders',
    body: 'SHE-HEALS is funded by the Global Cardiovascular Research Funders Forum (GCRFF) through its International Research Challenge on Women\u2019s Cardiovascular Health \u2014 a first-of-its-kind £7.4 million network grant. The women\u2019s cardiovascular health workstream is coordinated by the Heart and Stroke Foundation of Canada, with participating GCRFF funders that include the British Heart Foundation (BHF).',
    tags: [
      'Global Cardiovascular Research Funders Forum (GCRFF)',
      'Heart and Stroke Foundation of Canada',
      'British Heart Foundation (BHF)',
    ],
  },
  links: [
    { label: 'SHE-HEALS \u00b7 GCRFF', href: 'https://www.gcrff.org/she-heals' },
    { label: 'BHF announcement \u00b7 January 2026', href: 'https://www.bhf.org.uk/what-we-do/news-from-the-bhf/news-archive/2026/january/global-study-to-investigate-how-menopause-shapes-heart-health' },
    { label: 'GCRFF programme description (PDF)', href: 'https://www.bhf.org.uk/-/media/images/for-professionals/gcrff/programme-description_gcrff-irc.pdf' },
  ],
  contact: {
    title: 'Enquiries',
    body: 'For UK enquiries about the SHE-HEALS study \u2014 including participation, sponsor and press \u2014 please contact the Cambridge trial team.',
    // base64('cuh.sheheals@nhs.net') — decoded client-side to keep the address off raw HTML
    emailB64: 'Y3VoLnNoZWhlYWxzQG5ocy5uZXQ=',
  },
};

export const researchProcedures = {
  eyebrow: 'CAPABILITY & INFRASTRUCTURE',
  title: 'Research Procedures',
  tagline: 'A validated portfolio of vascular, haemodynamic and autonomic assessments delivered under Good Clinical Practice at Cambridge \u2014 supporting mechanistic Phase 1/2 trials, device validation and translational discovery.',
  intro: 'The Cheriyan Group operates a comprehensive suite of research procedures spanning endothelial function, blood pressure phenotyping, vascular structure, and autonomic assessment. All procedures are performed by trained investigators using calibrated, research-grade equipment, with standardised protocols suitable for regulatory submissions and multi-centre studies.',
  categories: [
    {
      key: 'endothelial',
      title: 'Endothelial Function',
      body: 'Gold-standard and complementary techniques to interrogate nitric-oxide bioavailability, arterial wall behaviour and endothelium-dependent vasodilatation \u2014 the earliest measurable signs of vascular disease.',
      procedures: [
        {
          name: 'Forearm Plethysmography',
          images: [
            {
              src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/n1jntqx6_image.png',
              alt: 'A supine participant with both forearms outstretched on padded supports, showing bilateral wrist BP cuffs, upper-arm occlusion cuffs and mercury-in-silastic strain gauges used for venous-occlusion forearm plethysmography.',
              caption: 'Bilateral forearm plethysmography setup',
            },
            {
              src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/zxipff5b_image.png',
              alt: 'Close-up of an antecubital fossa showing a fine 27-gauge brachial-artery cannula secured with sterile tape for continuous intra-arterial infusion of vasoactive study drugs.',
              caption: 'Fine brachial-artery cannula for intra-arterial infusion',
            },
          ],
          body: 'Venous-occlusion strain-gauge plethysmography for brachial-artery infusion studies of endothelium-dependent and -independent vasodilators.',
        },
        { name: 'Arterial (Aortic) Stiffness', body: 'Pulse wave analysis and carotid-femoral pulse wave velocity (SphygmoCor / Vicorder) as validated markers of central arterial stiffness and wave reflection.' },
        {
          name: 'Flow-Mediated Dilatation',
          images: [
            {
              src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/2xokvqcv_image.png',
              alt: 'A participant\u2019s arm supported on a foam wedge with a red upper-arm cuff inflated to suprasystolic pressure and a stereotactic-mounted linear ultrasound probe imaging the brachial artery during a flow-mediated dilatation measurement.',
              caption: 'Stereotactic-mounted probe imaging the brachial artery during FMD',
            },
          ],
          body: 'High-resolution brachial-artery ultrasound with automated edge-detection to quantify endothelium-dependent, NO-mediated vasodilatation.',
        },
      ],
    },
    {
      key: 'bp',
      title: 'Blood Pressure',
      body: 'Multi-modal, protocol-driven blood-pressure phenotyping for hypertension trials, device validation and clinical research \u2014 aligned with ESH, AHA and ISO 81060-2 recommendations.',
      procedures: [
        { name: 'Attended BP', body: 'Standardised clinic BP measurement under trained observation, following ESH/AHA positioning, cuff sizing and rest protocols.' },
        { name: 'Unattended BP', body: 'Automated office BP (AOBP) with the participant seated alone, avoiding white-coat effect \u2014 the SPRINT-style research standard.' },
        { name: '24-hour BP monitoring', body: 'Ambulatory BP monitoring across day and night periods, with dipping analysis, morning surge and BP variability.' },
        { name: 'Cuffless BP validation', body: 'Head-to-head validation of wearable, optical and PPG-based cuffless BP devices against reference standards, in line with ISO 81060-3 / IEEE 1708.' },
      ],
    },
    {
      key: 'vascular',
      title: 'Vascular Structure & Function',
      body: 'Deep phenotyping of the peripheral and carotid vasculature \u2014 combining classical pharmacology with modern imaging.',
      procedures: [
        {
          name: 'Hand Vein technique (Aellig)',
          images: [
            {
              src: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/tcj3xmvu_Screenshot%202026-08-03%20at%2017.25.01.png',
              alt: 'Composite figure of the Aellig hand-vein compliance technique: two photos of a participant\u2019s cannulated dorsal hand veins with the tripod-mounted Linear Variable Differential Transformer (LVDT) sensor and mercury sphygmomanometer, paired with schematic diagrams of the LVDT during cuff deflation and inflation and an example displacement-signal tracing showing the change in vein diameter (Δd) across successive cuff cycles.',
              caption: 'Aellig dorsal hand-vein compliance setup: cannulated setup, LVDT tripod schematic (deflation vs inflation) and example displacement tracing (Δd)',
            },
          ],
          body: 'Dorsal hand vein compliance technique (Aellig) for local, first-in-human pharmacology of venoconstrictor and venodilator agents.',
        },
        { name: 'Carotid IMT', body: 'B-mode ultrasound of the common carotid artery with semi-automated intima-media thickness measurement, an accepted surrogate for atherosclerotic burden.' },
      ],
    },
    {
      key: 'autonomic',
      title: 'Autonomic Function',
      body: 'Non-invasive quantification of cardiac autonomic control \u2014 informing arrhythmia, heart-failure and metabolic-disease research.',
      procedures: [
        { name: 'Heart Rate Variability', body: 'Time-domain, frequency-domain and non-linear HRV analysis from short- and long-term ECG recordings, following the Task Force of the ESC/NASPE standards.' },
        { name: 'Cloud-based real-time Holter monitoring', body: 'Continuous ambulatory ECG capture with real-time, cloud-based waveform review \u2014 enabling remote rhythm surveillance, rapid arrhythmia adjudication and low-friction integration into decentralised trials.' },
      ],
    },
  ],
  enquiries: {
    title: 'Enquiries',
    body: 'For sponsor, CRO or academic enquiries about incorporating Research Procedures into your protocol, please contact the Cambridge team.',
    // uses the shared research-EPS mailbox
  },
};

export const values = [
  'Rigour', 'Curiosity', 'Collaboration', 'Integrity',
  'Translation', 'Mentorship', 'Excellence',
  'Equity', 'Inclusivity', 'Openness',
];

export const team = [
  { name: 'Dr Joseph Cheriyan', role: 'Principal Investigator · Consultant', img: CHERIYAN_PHOTO, href: 'https://www.cuh.nhs.uk/staff-directory/joseph-cheriyan/' },
  { name: 'Navazh Jalaludeen', role: 'Consultant Cardiac Physiologist (Echocardiography)', img: '/team/navazh.jpg', href: '' },
  { name: 'Annette Hubsch', role: 'Senior Research Nurse', img: '/team/annette.jpg', href: '' },
  { name: 'Joanna Helmy', role: 'Senior Research Nurse', img: '/team/joanna.jpg', href: '' },
  { name: 'Elaine Amis', role: 'Senior Research Nurse', img: '/team/elaine.jpg', href: '' },
  { name: 'Roberto Cayado-Lopez', role: 'Senior Research Nurse', img: '/team/roberto.jpg', href: '' },
  {
    name: 'Dr Pablo Kostelec',
    role: 'Specialist Registrar · Clinical Pharmacology',
    img: '/team/pablo.jpg',
    href: '',
    slug: 'pablo-kostelec',
    subtitle: 'Specialist Registrar in Clinical Pharmacology & Internal Medicine · Clinician-Scientist · MD Candidate, University of Cambridge',
    bio: [
      "Dr Pablo Kostelec is a Clinical Pharmacology and Internal Medicine Specialist Registrar at Cambridge University Hospitals and a clinician-scientist undertaking an MD at the University of Cambridge. His research focuses on integrating artificial intelligence, digital biomarkers and emerging technologies into early-phase clinical trials to improve the prevention, detection and management of cardiovascular disease.",
      "Working within the Cheriyan Group and the IGNITE programme, his research spans the end-to-end translational pathway for medical innovation—from identifying unmet clinical needs and developing AI-enabled diagnostics, digital biomarkers and novel clinical trial methodologies, through clinical validation, experimental medicine and regulatory strategy, to implementation within healthcare systems. He works closely with academic, NHS and industry partners to accelerate the safe adoption of innovative technologies and therapeutics.",
      "Prior to commencing his doctoral studies, Pablo completed the University of Cambridge Master’s in Healthcare Data Informatics, Innovation and Commercialisation and <a href=\"https://www.jbs.cam.ac.uk/entrepreneurship/programmes/enterprisetech/\" target=\"_blank\" rel=\"noreferrer\">EnterpriseTECH</a> at Cambridge Judge Business School. He is also an alumnus of the <a href=\"https://nhscep.com/\" target=\"_blank\" rel=\"noreferrer\">NHS England Clinical Entrepreneur Programme</a> and the <a href=\"https://www.fmlm.ac.uk/services-programmes/national-medical-directors-clinical-fellow-scheme\" target=\"_blank\" rel=\"noreferrer\">National Medical Director’s Clinical Fellow Scheme</a>. Together, these experiences underpin his interest in the end-to-end translational pathway for medical innovation—from identifying unmet clinical needs and developing novel technologies, through clinical validation and regulatory strategy, to commercialisation, health-system adoption and real-world clinical impact.",
      "Alongside his research, Pablo contributes to the design and delivery of first-in-human and proof-of-concept clinical trials while providing specialist clinical care in Clinical Pharmacology, Internal Medicine and cardiovascular medicine at Cambridge University Hospitals.",
    ],
  },
  { name: 'Dr Ing Ni Lu', role: 'Clinical PhD Candidate · Specialist Registrar · Clinical Pharmacology', img: '/team/ingni.jpg', href: '' },
  { name: 'Dr Rouchelle Sriranjan', role: 'NIHR Clinical Lecturer · Cardiology', img: '/team/rouchelle.jpg', href: '' },
  { name: 'Dr Oliver Devine', role: 'Academic Clinical Fellow · Clinical Pharmacology', img: '/team/oliver.jpg', href: '' },
  { name: 'Dr Mustafa Khan', role: 'Clinical Fellow · Clinical Pharmacology', img: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/r6qqsroa_1786640723238.png', href: '' },
  { name: 'Dr Kevin Joseph', role: 'Clinical Fellow · Acute Medicine', img: '/team/kevin.jpg', href: '' },
  { name: 'Dr Adebowale Afolayan', role: 'Specialist Registrar · Clinical Pharmacology', img: '/team/adebowale.jpg', href: '' },
  { name: 'Eleanor Evans-Evans', role: 'Senior Clinical Research and Education Manager', img: '/team/eleanor.jpg', href: '' },
  { name: 'Bruno Matos', role: 'Senior Research Echocardiographer', img: '/team/bruno.jpg', href: '' },
];

export const trialsOfficeTeam = [
  { name: 'Sonakshi Kadyan', role: 'CV Trials Office Manager', img: '/team/sonakshi.jpg', href: '' },
  { name: 'Heike Templin', role: 'Senior Clinical Trials Coordinator', img: '/team/heike.jpg', href: '' },
  { name: 'Zehra Yilmaz', role: 'Senior Clinical Trials Coordinator', img: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/kyw1e4n4_1785774445342.png', href: '' },
  { name: 'Guneswary Thaygaraja', role: 'Clinical Trials Coordinator', img: '/team/guneswary.jpg', href: '' },
  { name: 'Geri Barrett', role: 'Clinical Trials Coordinator', img: '/team/geri.jpg', href: '' },
  { name: 'Barbara Masango', role: 'Data Manager', img: '/team/barbara.jpg', href: '' },
  { name: 'Stephani Mok', role: 'Data Manager', img: '/team/stephani.jpg', href: '' },
];

export const traineesSupervised = [
  // Current (2024–present, 2025–present)
  { name: 'Dr Ing Ni Lu', degree: 'PhD', project: 'Zibotentan and dapagliflozin in hypertensive patients', role: 'Co-Supervisor', years: '2025–present', current: true },
  { name: 'Dr Pablo Kostelec', degree: 'MD', project: 'STEREO — Utilising AI Analysis of Sounds to Predict Heart Failure Decompensation', role: 'Co-Supervisor', years: '2024–present', current: true },
  // Past — with current position + profile link
  { name: 'Dr Spoorthy Kulkarni', degree: 'PhD', project: 'The Role of Relaxin in Vascular Physiology', role: 'Co-Supervisor', years: '2024–2026', currentPosition: 'Clinical Pharmacologist · Cambridge University Hospitals', href: 'https://www.linkedin.com/in/dr-spoorthy-kulkarni-67468aa4' },
  { name: 'Dr Ross Davidson', degree: 'MPhil', project: 'Assessment of Inflammation in Atrial Fibrillation using FDG-PET-CT Imaging', role: 'Co-Supervisor', years: '2024–2025' },
  { name: 'Dr Rouchelle Sriranjan', degree: 'PhD', project: 'Low-dose IL-2 in Acute Coronary Syndrome', role: 'Co-Supervisor', years: '2021–2024', currentPosition: 'NIHR Clinical Lecturer · Cardiology, University of Cambridge', href: 'https://www.linkedin.com/in/rouchelle-sriranjan' },
  { name: 'Dr James Goodman', degree: 'PhD', project: 'Dual Agonism at the Glucagon and GLP-1 Receptor', role: 'Co-Supervisor', years: '2020–2023', currentPosition: 'Consultant Physician · Cambridge University Hospitals', href: 'https://www.linkedin.com/in/james-goodman-44098b34' },
  { name: 'Dr Tian Zhao', degree: 'PhD', project: 'Low-dose IL-2 Dose Escalation in Stable IHD and ACS', role: 'Co-Supervisor', years: '2018–2021', currentPosition: 'BHF Intermediate Clinical Research Fellow · Consultant Interventional Cardiologist, Royal Papworth', href: 'https://www.linkedin.com/in/tian-zhao-1b8440119' },
  { name: 'Dr Paul Cacciottolo', degree: 'MD', project: 'Investigating Vascular Benefits from LDL Cholesterol Lowering with Alirocumab', role: 'Co-Supervisor', years: '2018–2021', currentPosition: 'Consultant Cardiologist & Clinical Director · CUH', href: 'https://www.linkedin.com/in/paul-cacciottolo-06381b47' },
  { name: 'Dr Petra Sulentic', degree: 'PhD', project: 'Evaluation of Metabolic and Cardiovascular Actions of [Pyr¹] Apelin-13', role: 'Co-Supervisor', years: '2018–2021', currentPosition: 'Senior Medical Director & Global Clinical Program Lead · AstraZeneca', href: 'https://www.linkedin.com/in/petra-sulentic-921a29108' },
  { name: 'Dr Takahiro Yamamoto', degree: 'PhD', project: 'Quantitative Systems Models of Human Cardiovascular Physiology', role: 'Co-Supervisor', years: '2016–2019', currentPosition: 'Senior Research Physician & FIH Principal Investigator · Hammersmith Medicines Research', href: 'https://www.linkedin.com/in/takahiro-yamamoto-a5b411b1' },
  { name: 'Dr Marie Fisk', degree: 'PhD', project: 'Inflammation in Cardiovascular Manifestations of COPD', role: 'Co-Supervisor', years: '2012–2015', currentPosition: 'Clinical Lecturer, Department of Medicine · Consultant, Respiratory & Acute Medicine, Cambridge', href: 'https://www.cardiovascular.cam.ac.uk/directory/f' },
  { name: 'Dr Lucy Yang', degree: 'PhD', project: 'Epoxyeicosatrienoic Acids in the Cardiovascular System', role: 'Co-Supervisor', years: '2012–2015', currentPosition: 'Anaesthetic Registrar · Royal Free London / UCLH', href: 'https://www.linkedin.com/in/lucy-liu-yang-086a977b' },
  { name: 'Dr Joshua Wortzel', degree: 'MPhil', project: 'Analysis of PET-CT Associated Inflammation in Atherosclerosis, COPD and RA', role: 'Supervisor', years: '2013–2014', currentPosition: 'Child & Adolescent Psychiatrist · Hartford HealthCare; Adjunct Assistant Professor · Yale', href: 'https://www.linkedin.com/in/joshua-wortzel-7131a097' },
  { name: 'Dr Parag Gajendragadkar', degree: 'MPhil (Translational Medicine)', project: 'Effects of Oral Lycopene Supplementation on Vascular Function', role: 'Supervisor', years: '2009–2012', currentPosition: 'Consultant Cardiologist & Electrophysiologist · Royal Papworth', href: 'https://www.linkedin.com/in/parag-gajendragadkar-38414886' },
];

// Legacy alias kept for backwards compatibility
export const pastTrainees = traineesSupervised;

export const news = [
  {
    slug: 'she-heals-gcrff-women-cardiovascular-2026',
    tag: 'Award · 2026',
    pinned: true,
    title: 'SHE-HEALS — £7.4M GCRFF award to transform women\u2019s cardiovascular health',
    excerpt: 'The Global Cardiovascular Research Funders Forum has awarded £7.4 million to SHE-HEALS (Stopping atherosclerotic Heart and vEssel disease in women) \u2014 a five-year, thirteen-institution study co-led from the University of Cambridge (Prof Ziad Mallat) and the University of Melbourne (Prof Martha Hickey), with Dr Joseph Cheriyan as Grant Co-applicant and Trial Chief Investigator. The programme will characterise the vascular biology of the perimenopause and deliver the largest trial of its kind to detect silent atherosclerosis in women in perimenopause and early after menopause \u2014 testing whether earlier prevention can halt or reverse disease progression before symptoms appear.',
    url: 'https://www.bhf.org.uk/what-we-do/news-from-the-bhf/news-archive/2026/january/global-study-to-investigate-how-menopause-shapes-heart-health',
    date: '2026-01-20',
    href: '/she-heals',
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_clinical-lab-hub-2/artifacts/g506i86j_IMG-20260729-WA0012.jpg',
  },
  {
    slug: 'in-ear-audio-cardiac-stroke-volume-nature-comms-2026',
    tag: 'Publication · 2026',
    title: 'Measuring cardiac stroke volume through in-ear audio sensing — Nature Communications',
    excerpt: 'Butkow KJ, Jalaludeen N (co-first), Liu Y, Stuchbury-Wass J, Yang Q, Ciliberto M, Ma D, Cheriyan J, Mascolo C. Nature Communications. 2026;17 July · doi:10.1038/s41467-026-75642-0. A deep-learning system that estimates cardiac stroke volume from in-ear audio captured by ordinary earbuds — mean absolute error 5.24 ml, r = 0.94 vs clinical reference — paving the way for longitudinal, scalable, out-of-clinic cardiovascular monitoring. Collaboration between the Cheriyan Group / Cardiovascular Trials Office (CUH) and the Department of Computer Science and Technology, University of Cambridge (Prof. Cecilia Mascolo).',
    url: 'https://www.nature.com/articles/s41467-026-75642-0',
    doi: '10.1038/s41467-026-75642-0',
    image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'ivory-il2-nature-medicine-2026',
    tag: 'Publication · 2026',
    title: 'Anti-inflammatory therapy with low-dose IL-2 in acute coronary syndromes — the IVORY trial',
    excerpt: 'Randomised Phase 2 evidence in Nature Medicine (2026;32:624-632) that low-dose IL-2 modulates vascular inflammation after acute coronary syndrome.',
    doi: '10.1038/s41591-025-04090-y',
    url: 'https://doi.org/10.1038/s41591-025-04090-y',
    pmid: '41507574',
    image: 'https://images.unsplash.com/photo-1706639449579-b28503bf026d?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'cambridge-news-ivory-immune-heart-attack-2026',
    tag: 'Media · 2026',
    title: 'Targeting the immune system could prevent future heart attacks, clinical trial suggests — University of Cambridge',
    excerpt: 'University of Cambridge Research News (8 January 2026) covers the IVORY trial: Cambridge researchers show that low-dose IL-2 boosts protective regulatory T-cells in patients after acute coronary syndrome, reducing blood-vessel inflammation and potentially the risk of future heart attacks. Features Dr Joseph Cheriyan and colleagues.',
    url: 'https://www.cam.ac.uk/research/news/targeting-the-immune-system-could-prevent-future-heart-attacks-clinical-trial-suggests',
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'launch-htn-lorundrostat-jama-2025',
    tag: 'Trial · 2025',
    title: 'LAUNCH-HTN — Lorundrostat in treatment-resistant hypertension published in JAMA',
    excerpt: 'Global Phase 3 RCT demonstrating aldosterone-synthase inhibition for uncontrolled hypertension. JAMA. 2025;334(5):409–418.',
    image: 'https://images.unsplash.com/photo-1666214280352-db292c05fd80?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'late-breaking-esc-acc-2025',
    tag: 'Event · 2025',
    title: 'Late-Breaking Clinical Trials — ESC & ACC congresses',
    excerpt: 'Early-career researchers from the group presented late-breaking clinical trial results at the European Society of Cardiology and American College of Cardiology congresses.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'cambridge-news-anti-inflammatory-heart-attack-2024',
    tag: 'Media · 2024',
    title: 'Anti-inflammatory drug could reduce future heart attack risk — University of Cambridge',
    excerpt: 'University of Cambridge Research News (2 September 2024) covering the first presentation of IVORY trial data at the European Society of Cardiology (ESC) Congress: low-dose IL-2 modulates vascular inflammation after acute coronary syndrome, potentially reducing the risk of future heart attacks. Features Dr Joseph Cheriyan and the Cheriyan Group.',
    url: 'https://www.cam.ac.uk/research/news/anti-inflammatory-drug-could-reduce-future-heart-attack-risk',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'tactic-r-baricitinib-lancet-respir-2023',
    tag: 'Publication · 2023',
    title: 'TACTIC-R baricitinib / ravulizumab published in Lancet Respiratory Medicine',
    excerpt: 'Randomised parallel-arm open-label Phase 4 trial in severe COVID-19 delivered across UK sites during the pandemic.',
    image: 'https://images.unsplash.com/photo-1518152006812-edab29b069ac?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'nejm-evidence-il2-treg-2022',
    tag: 'Media · 2022',
    title: 'NEJM Evidence — Regulatory T-cell response to low-dose IL-2 in ischaemic heart disease',
    excerpt: 'The inaugural clinical readout of LILACS/IVORY, published in the launch year of NEJM Evidence.',
    image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'science-hydroxychloroquine-prolific-2020',
    tag: 'Media · 2020',
    title: 'Science — Big studies dim hopes for hydroxychloroquine (Kupferschmidt, June 2020)',
    excerpt: 'Kupferschmidt K. Science. 2020;368(6496):1166-1167 · doi:10.1126/science.368.6496.1166. Science magazine\'s June 2020 feature on the unravelling case for hydroxychloroquine as a putative COVID-19 therapy — referencing the Cambridge-led PROLIFIC trial (Cheriyan et al.), which was paused by the UK Medicines and Healthcare products Regulatory Agency (MHRA) amid emerging safety and efficacy concerns from parallel international studies.',
    url: 'https://www.science.org/doi/10.1126/science.368.6496.1166',
    image: 'https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?auto=format&fit=crop&w=900&q=80',
  },
  {
    slug: 'cambridge-news-tomato-pill-vascular-2014',
    tag: 'Media · 2014',
    title: 'Lycopene in cardiovascular disease — University of Cambridge',
    excerpt: 'University of Cambridge Research News (10 June 2014): a daily supplement of lycopene, the natural pigment found in tomatoes, may improve blood-vessel function in patients with cardiovascular disease. Coverage of the group\'s early experimental-medicine work on endothelial function.',
    url: 'https://www.cam.ac.uk/research/news/tomato-pill-improves-function-of-blood-vessels',
    image: 'https://images.unsplash.com/photo-1546470427-e91334c8e6d0?auto=format&fit=crop&w=900&q=80',
  },
];

export const journals = [
  'Nature Medicine', 'JAMA', 'Circulation', 'NEJM Evidence',
  'Hypertension', 'Lancet Respir Med', 'JACC', 'Nature Communications',
];

export const publicationLinks = {
  orcid: 'https://orcid.org/0000-0001-6921-1592',
  scholar: 'https://scholar.google.com/citations?user=zILR5f0AAAAJ&hl=en',
  pubmed: 'https://pubmed.ncbi.nlm.nih.gov/?term=Cheriyan+j+and+cambridge+or+cheriyan+j+and+gsk&sort=date',
  cambridge: 'https://www.cardiovascular.cam.ac.uk/directory/jcheriyan',
};

export const keyPapers = [
  'Sriranjan-Rothwell RS, et al. Anti-inflammatory therapy with low-dose IL-2 in acute coronary syndromes: a randomized phase 2 trial. Nature Medicine. 2026;32:624-632.',
  'Saxena M, et al. Lorundrostat in Participants with Uncontrolled Hypertension and Treatment Resistant Hypertension: The LAUNCH-HTN Randomized Controlled Trial. JAMA. 2025;334(5):409-418.',
  'Zhao T, et al. Regulatory T-Cell Response to Low-Dose Interleukin-2 in Ischemic Heart Disease. NEJM Evidence. 2022;1(1).',
  'Mäki-Petäjä KM, et al. Mechanisms Underlying VEGFR Inhibition-Induced Hypertension: The HYPAZ Trial. Hypertension. 2021;77(5):1591-1599.',
  'Mäki-Petäjä KM, Elkhawad M, Cheriyan J, et al. Anti-TNF-α therapy reduces aortic inflammation and stiffness in RA. Circulation. 2012;126(21):2473-80.',
  'Cheriyan J, Webb AJ, et al. Inhibition of p38 MAPK improves NO-mediated vasodilatation and reduces inflammation in hypercholesterolemia. Circulation. 2011;123(5):515-23.',
];

export const indicators = [
  { label: 'Publications', value: '78' },
  { label: 'Students co-supervised', value: '15' },
  { label: 'Altmetric mentions', value: '900+' },
];

export const contact = {
  email: 'jc403@cam.ac.uk',
  linkedin: 'https://www.linkedin.com/in/josephcheriyan/',
  scholar: 'https://scholar.google.co.uk/citations?user=zILR5f0AAAAJ',
  orcid: 'https://orcid.org/0000-0001-6921-1592',
  cambridge: 'https://www.cardiovascular.cam.ac.uk/directory/jcheriyan',
  nhs: 'https://www.cuh.nhs.uk/staff-directory/joseph-cheriyan/',
  address: [
    'Box 98, Level 3',
    'ACCI Building',
    'Cambridge University Hospitals NHS FT',
    'Hills Rd',
    'Cambridge CB2 0QQ',
  ],
};

export const ignite = {
  name: 'IGNITE',
  acronym: 'Innovative Group for Novel Interventions, Trials & Experimental Medicine',
  tagline:
    "IGNITE is the innovation and experimental-medicine programme of the Cheriyan Group, bringing together artificial intelligence, digital biomarkers, emerging technologies and novel trial methodologies to transform how cardiovascular disease is studied, detected, monitored and treated.",
  intro:
    'Our work spans AI-enabled and digital biomarkers, adaptive and decentralised clinical trials, intelligent trial design, and precision therapeutics — translating emerging technologies into rigorous clinical research and experimental medicine.',
  intro2:
    'Working in partnership with academia, the NHS and industry, and drawing on Cambridge’s world-leading ecosystem in AI, biomedical science and drug development, IGNITE develops and evaluates next-generation approaches to clinical trials, disease detection and monitoring, and therapeutic development.',
  pillars: [
    {
      key: 'ai',
      title: 'AI & Computational Medicine',
      items: [
        'Machine learning',
        'Foundation models',
        'Digital twins',
        'Clinical decision support',
      ],
    },
    {
      key: 'digital',
      title: 'Digital Biomarkers',
      items: [
        'Voice biomarkers',
        'Respiratory acoustics',
        'Facial video biomarkers',
        'Wearables',
        'Passive sensing',
        'Remote monitoring',
      ],
    },
    {
      key: 'trials',
      title: 'Innovative Clinical Trials',
      items: [
        'Early-phase studies',
        'Adaptive platform trials',
        'Decentralised and hybrid trials',
        'Bayesian methods',
        'Digital endpoints',
      ],
    },
    {
      key: 'cv',
      title: 'Cardiovascular Therapeutics',
      items: [
        'Heart failure',
        'Hypertension',
        'Lipids',
        'Inflammation',
        'Prevention',
        'Cardiometabolic disease',
      ],
    },
  ],
  whyCambridge: {
    title: 'Why Cambridge?',
    body:
      'Cambridge offers one of the world’s most concentrated environments for healthcare innovation. IGNITE leverages this unique ecosystem by bringing together:',
    partners: [
      {
        name: 'University of Cambridge',
        description:
          'Internationally recognised expertise in AI, engineering, data science and biomedical research.',
      },
      {
        name: 'Cambridge University Hospitals NHS Foundation Trust',
        description:
          'A leading centre for cardiovascular care, translational research and early-phase clinical trials.',
      },
      {
        name: 'Cambridge Biomedical Campus',
        description:
          'One of Europe’s largest life sciences campuses, co-locating academia, the NHS, global pharmaceutical companies and biotechnology innovators.',
      },
      {
        name: 'Industry partnerships',
        description:
          'Collaborations spanning pharmaceutical development, digital health, medical devices and AI, enabling rapid evaluation and translation of emerging technologies into clinical practice.',
      },
    ],
  },
  studies: {
    heading: 'Studies',
    intro:
      'IGNITE leads and contributes to studies applying AI, wearable sensors and digital biomarkers to the early detection and management of cardiovascular disease.',
    completed: [
      {
        title: 'Measuring cardiac stroke volume through in-ear audio sensing',
        acronym: '',
        nct: '',
        sponsor: 'University of Cambridge · Cambridge University Hospitals NHS Foundation Trust',
        role: 'Cambridge Principal Investigator (clinical): Dr Joseph Cheriyan · Co-first author: Navazh Jalaludeen (CUH) · Senior author: Prof. Cecilia Mascolo (Computer Science and Technology, Cambridge)',
        description:
          'A deep-learning system that estimates cardiac stroke volume directly from the audio picked up by ordinary in-ear earbuds. Data from 23 healthy participants yielded a mean absolute error of 5.24 ml, a Pearson correlation of r = 0.94 versus a clinically validated reference, and a percentage-error limit-of-agreement of 11.05% — within accepted clinical range. Published open-access in Nature Communications, 17 July 2026.',
        years: '2024 – 2026',
        enrolment: '23 healthy volunteers',
        href: 'https://www.nature.com/articles/s41467-026-75642-0',
      },
      {
        title: 'Multidomain endpoints of an ECG patch in acute decompensated heart failure',
        acronym: '',
        nct: 'NCT06243029',
        sponsor: 'AstraZeneca',
        role: 'Cambridge Principal Investigator: Dr Joseph Cheriyan',
        description:
          'A three-centre UK observational study evaluating a wearable ECG patch as a remote, non-invasive tool to capture multidomain physiological endpoints in patients recovering from acute decompensated heart failure. Findings will inform the design of future heart failure drug-development programmes.',
        years: '2024 – 2026',
        enrolment: '54 participants',
        href: 'https://clinicaltrials.gov/study/NCT06243029',
      },
      {
        title: 'Mechanisms Underlying Vascular Endothelial Growth Factor Receptor Inhibition-Induced Hypertension — the HYPAZ Trial',
        acronym: 'HYPAZ',
        nct: '',
        sponsor: 'University of Cambridge · Cambridge University Hospitals NHS Foundation Trust · GlaxoSmithKline',
        role: 'Senior author: Dr Joseph Cheriyan · First author: Dr Kaisa Mäki-Petäjä · AI / computational image analysis: Prof. Carola-Bibiane Schönlieb, Dr Yoeri Boink & Dr Christoph Brune (Cambridge Image Analysis Group, DAMTP)',
        description: 'Prospective mechanistic experimental-medicine trial in oncology patients receiving pazopanib, a vascular endothelial growth factor receptor (VEGFR) tyrosine-kinase inhibitor, to interrogate the vascular mechanisms of anti-angiogenic drug-induced hypertension. Combined invasive haemodynamics, endothelial-function testing and AI-based retinal image analysis (in collaboration with the Cambridge Image Analysis Group at DAMTP) to quantify microvascular structural change. Published in Hypertension, 2021;77(5):1591-1599.',
        years: '2016 – 2021',
        enrolment: 'Experimental-medicine cohort',
        href: 'https://www.ahajournals.org/doi/10.1161/HYPERTENSIONAHA.120.16454',
      },
    ],
    ongoing: [
      {
        title: 'Utilising AI Analysis of Sounds To prEdict heaRt failurE decOmpensation',
        acronym: 'STEREO',
        nct: 'NCT06555757',
        sponsor: 'Cambridge University Hospitals NHS Foundation Trust · University of Cambridge',
        role: 'Chief Investigator: Dr Joseph Cheriyan',
        description:
          'A prospective observational study developing a smartphone-based AI algorithm that detects early pulmonary congestion in heart failure from subtle changes in voice, cough and breathing sounds. Two-part study — a 50-patient pilot followed by a 200-patient definitive cohort — to enable earlier community-based intervention and reduce emergency admissions.',
        years: '2024 – 2027',
        enrolment: 'Up to 250 participants (recruiting)',
        href: 'https://clinicaltrials.gov/study/NCT06555757',
      },
    ],
  },
  collaborators: [
    { name: 'Professor Ziad Mallat', role: 'BHF Professor of Cardiovascular Medicine · Heart & Lung Research Institute', href: 'https://www.hlri.cam.ac.uk/staff/professor-ziad-mallat' },
    { name: 'Dr Stephen Hoole', role: 'Consultant Interventional Cardiologist · Royal Papworth Hospital', href: 'https://www.cardiovascular.cam.ac.uk/directory/shoole' },
    { name: 'Professor James Rudd', role: 'Professor of Cardiovascular Medicine · Department of Medicine', href: 'https://www.cardiovascular.cam.ac.uk/directory/jrudd' },
    { name: 'Dr Jason Tarkin', role: 'Clinical Lecturer & Honorary Consultant Cardiologist', href: 'https://www.cardiovascular.cam.ac.uk/directory/jason-tarkin' },
    { name: 'Professor Ian Wilkinson', role: 'Professor of Therapeutics · Director, Cambridge Clinical Trials Unit', href: 'https://www.cardiovascular.cam.ac.uk/directory/iwilkinson' },
    { name: 'Professor Carmel McEniery', role: 'Professor of Cardiovascular Medicine · Vascular Research Clinics', href: 'https://www.cardiovascular.cam.ac.uk/directory/cmceniery' },
    { name: 'Dr Ari Ercole', role: 'Affiliated Associate Professor · Division of Anaesthesia · Magdalene College', href: 'https://www.magd.cam.ac.uk/people/ari-ercole' },
    { name: 'Professor Christi Deaton', role: 'Emerita Professor of Nursing · Primary Care Unit, University of Cambridge', href: 'https://www.phpc.cam.ac.uk/staff/professor-christi-deaton' },
    { name: 'Professor Cecilia Mascolo', role: 'Professor of Mobile Systems · Department of Computer Science and Technology, Cambridge', href: 'https://www.cst.cam.ac.uk/people/cm542' },
    { name: 'Professor Roberto Cipolla', role: 'Professor of Information Engineering · Department of Engineering, Cambridge', href: 'https://mi.eng.cam.ac.uk/~cipolla/' },
    { name: 'Professor Carola-Bibiane Schönlieb', role: 'Professor of Applied Mathematics · Head, Cambridge Image Analysis Group · DAMTP, University of Cambridge', href: 'https://www.damtp.cam.ac.uk/user/cbs31/' },
    { name: 'Professor Mateo Aboy', role: 'Professor of Innovation, Patents & Regulation · Centre for Law, Medicine and Life Sciences, Cambridge', href: 'https://www.lml.law.cam.ac.uk/people/Research-Scholars-Associates/Prof-mateo-aboy' },
    { name: 'Professor James Galloway', role: 'Professor of Rheumatology · Centre for Rheumatic Diseases, King\'s College London', href: 'https://www.kcl.ac.uk/people/james-galloway' },
    { name: 'Professor Andrew Cope', role: 'Head, Centre for Rheumatic Diseases · King\'s College London', href: 'https://www.kcl.ac.uk/people/andrew-cope' },
    { name: 'Professor Phil Chowienczyk', role: 'Professor of Clinical Pharmacology · King\'s College London · Guy\'s and St Thomas\' NHS Foundation Trust', href: 'https://www.kcl.ac.uk/people/philip-chowienczyk' },
    { name: "Dr Jamie O'Driscoll", role: 'Associate Professor of Exercise and Cardiovascular Physiology · University of Leicester', href: 'https://le.ac.uk/people/jamie-o-driscoll' },
  ],
};
// ContentProvider fetches the DB content and calls applyContent(...) before
// first render. This mutates the exported objects/arrays IN PLACE so all
// components that `import { hero } from '../mock'` transparently see the
// database values without any refactor.
//
// Keep this map in sync with initial_content.py on the backend.

const _mutableRegistry = {
  site,
  partnerLogos,
  hero,
  affiliations,
  about,
  positions,
  pastRoles,
  memberships,
  researchSummary,
  researchThemes,
  projectActivity,
  cto,
  echoService,
  teaching,
  featuredTrial,
  trials,
  values,
  team,
  trialsOfficeTeam,
  pastTrainees,
  traineesSupervised,
  news,
  journals,
  publicationLinks,
  keyPapers,
  indicators,
  contact,
  ignite,
  sheHeals,
  researchProcedures,
};

function _replaceArray(target, source) {
  target.splice(0, target.length, ...source);
}

function _replaceObject(target, source) {
  for (const k of Object.keys(target)) delete target[k];
  Object.assign(target, source);
}

export function applyContent(data) {
  if (!data || typeof data !== 'object') return;
  for (const [key, value] of Object.entries(data)) {
    const target = _mutableRegistry[key];
    if (target === undefined || value === undefined || value === null) continue;
    if (Array.isArray(target) && Array.isArray(value)) {
      _replaceArray(target, value);
    } else if (typeof target === 'object' && typeof value === 'object') {
      _replaceObject(target, value);
    }
  }
}

export const CONTENT_KEYS = Object.keys(_mutableRegistry);
